import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../../utils';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  size?: 'sm' | 'md' | 'lg';
  onClear?: () => void;
  label?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      size = 'md',
      onClear,
      onChange,
      value,
      label,
      id,
      placeholder = 'Search...',
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const currentValue = value !== undefined ? value : internalValue;
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const handleClear = () => {
      if (value === undefined) {
        setInternalValue('');
      }
      onClear?.();
      const input = ref && 'current' in ref ? ref.current : null;
      if (input) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(input, '');
        const event = new Event('input', { bubbles: true });
        input.dispatchEvent(event);
      }
    };

    const sizeClasses = {
      sm: 'h-8 pl-9 pr-9 text-sm',
      md: 'h-10 pl-11 pr-10 text-sm',
      lg: 'h-12 pl-12 pr-11 text-base',
    };

    const iconSizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-5 w-5',
    };

    const iconPositionClasses = {
      sm: 'left-2.5',
      md: 'left-3',
      lg: 'left-3.5',
    };

    const clearButtonPositionClasses = {
      sm: 'right-2.5',
      md: 'right-3',
      lg: 'right-3.5',
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <Search
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-text-placeholder z-10 pointer-events-none',
              iconSizeClasses[size],
              iconPositionClasses[size]
            )}
            aria-hidden="true"
          />
          <input
            ref={ref}
            id={inputId}
            type="text"
            value={currentValue}
            onChange={handleChange}
            placeholder={placeholder}
            className={cn(
              'w-full bg-[var(--field-bg)] text-text-primary placeholder-text-placeholder',
              'border border-border rounded-lg',
              'transition-all duration-150',
              'focus:outline-none focus:border-info-500 focus:ring-2 focus:ring-info-500/20',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-surface-2',
              sizeClasses[size],
              className
            )}
            role="searchbox"
            aria-label={label || 'Search'}
            {...props}
          />
          {currentValue && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'absolute top-1/2 -translate-y-1/2',
                'p-1 rounded-sm',
                'text-text-placeholder hover:text-text-muted',
                'focus:outline-none focus:ring-1 focus:ring-border-hover',
                'transition-colors',
                clearButtonPositionClasses[size]
              )}
              aria-label="Clear search"
            >
              <X className={iconSizeClasses[size]} />
            </button>
          )}
        </div>
      </div>
    );
  }
);
SearchInput.displayName = 'SearchInput';
